/**
 * @file useLocalFontSample.ts
 * @description Образец «Аа Бб Вв» выбранного, ещё не загруженного файла
 * шрифта (phase19.16 Р1-7; проект uiux §3): файл читается в браузере и
 * подключается через `FontFace` под временным именем — на сервер для этого
 * ничего не уходит. Браузер не прочитал файл (или нет `FontFace`, как в
 * тестах) — образец системным шрифтом.
 *
 * Результат хранится вместе с файлом (`{ file, family }`): для другого файла
 * он просто не подходит, сбрасывать состояние в эффекте не нужно. Шрифт,
 * дочитанный после смены файла или ухода с экрана, из `document.fonts`
 * удаляется сразу — без утечки.
 */

import { useEffect, useState } from "react";

let counter = 0;

/** `font-family` образца выбранного файла; `null` — пока не готов или не прочитался. */
export function useLocalFontSample(file: File | null): string | null {
  const [ready, setReady] = useState<{ file: File; family: string } | null>(null);

  useEffect(() => {
    if (!file || typeof FontFace === "undefined" || !document.fonts) return;
    let cancelled = false;
    let face: FontFace | null = null;
    const name = `commora-local-font-${++counter}`;
    void (async () => {
      try {
        const loaded = await new FontFace(name, await file.arrayBuffer()).load();
        if (cancelled) return;
        face = loaded;
        document.fonts.add(loaded);
        setReady({ file, family: `"${name}", system-ui, sans-serif` });
      } catch {
        // Не прочитался — образец останется системным, проверку делает сервер.
      }
    })();
    return () => {
      cancelled = true;
      if (face) document.fonts.delete(face);
    };
  }, [file]);

  return ready !== null && ready.file === file ? ready.family : null;
}
